import { useCallback, useEffect, useState } from "react";
import { useDevice } from "./useDevice";

export type IFullscreenState = {
    document: boolean;
    css: boolean;
};

export const useFullscreen = (defaultValue: IFullscreenState) => {
    const { isMobileDevice } = useDevice();
    const [fullscreen, setIsFullscreen] = useState<IFullscreenState>(defaultValue);

    const isSupported = (element: HTMLElement) =>
        Boolean(document.fullscreenEnabled) || Boolean(element.requestFullscreen);

    const exitFullScreen = useCallback(async () => {
        if (!document.fullscreenElement) return;

        try {
            await document.exitFullscreen();
        } catch (error) {
            console.error("Failed to exit fullscreen:", error);
        }
    }, []);

    const enterFullScreen = useCallback(async (element: HTMLElement) => {
        if (!element || !isSupported(element)) return;

        try {
            await element.requestFullscreen();
        } catch (error) {
            console.error("Failed to enter fullscreen:", error);
        }
    }, []);

    const toggleFullscreen = useCallback(
        async (element: HTMLElement) => {
            if (!isMobileDevice && isSupported(element)) {
                const isDocumentFullscreenActive = Boolean(document.fullscreenElement);

                if (isDocumentFullscreenActive) {
                    void exitFullScreen();
                } else {
                    await enterFullScreen(element);
                }
            } else {
                setIsFullscreen((state) => ({ ...state, css: !state.css }));
            }
        },
        [enterFullScreen, exitFullScreen, isMobileDevice],
    );

    useEffect(() => {
        const handleFullscreenChange = () => {
            const isActive = Boolean(document.fullscreenElement);

            setIsFullscreen((state) => ({ ...state, document: isActive }));
        };

        document.addEventListener("fullscreenchange", handleFullscreenChange);

        return () => {
            document.removeEventListener("fullscreenchange", handleFullscreenChange);
        };
    }, []);

    return {
        fullscreen,
        setIsFullscreen,
        toggleFullscreen,
    };
};
